import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { RsjInstructionCommitmentDto } from '../../dto/rsj-instruction-commitment.dto';
import { RsjInstructionEngagementDto } from '../../dto/rsj-instruction-engagement.dto';

@Component({
  selector: 'app-rsj-instruction-commitment',
  templateUrl: './rsj-instruction-commitment.component.html',
})
export class RsjInstructionCommitmentComponent implements OnInit, OnDestroy {
  private destroy$: Subject<void> = new Subject();

  public commitmentForm: UntypedFormGroup;
  public updated: boolean = false;
  public submitted: boolean = false;

  @Input() public rsjCommitment: RsjInstructionCommitmentDto;
  @Input() public disabled: boolean;

  constructor(private readonly formBuilder: UntypedFormBuilder) {}

  public ngOnInit(): void {
    this.initForm();
    this.initValueChangeHandlers();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onSubmit(): boolean {
    if (this.updated) {
      this.submitted = true;
    }
    return !this.updated || this.commitmentForm.valid;
  }

  public getDto(): RsjInstructionCommitmentDto {
    if (this.commitmentForm.valid) {
      this.rsjCommitment = {
        structureLabel: this.commitmentForm.controls.structureLabel.value,
        structureAddress: this.commitmentForm.controls.structureAddress.value,
        structureAdditionalAddress: this.commitmentForm.controls.structureAdditionalAddress.value,
        structureCity: this.commitmentForm.controls.structureCity.value,
        structurePostalCode: this.commitmentForm.controls.structurePostalCode.value,
        structurePhone: this.commitmentForm.controls.structurePhone.value,
        referentName: this.commitmentForm.controls.referentName.value,
        referentPhone: this.commitmentForm.controls.referentPhone.value,
        commitmentForNext3Months: this.commitmentForm.controls.commitmentForNext3Months.value,
        situationComment: {
          id: this.rsjCommitment?.situationComment?.id,
          youngPeopleProject: this.commitmentForm.get('situationComment')?.get('youngPeopleProject')?.value,
          youngPeopleSteps: this.commitmentForm.get('situationComment')?.get('youngPeopleSteps')?.value,
          proceduresProposedAdvisor: this.commitmentForm.get('situationComment')?.get('proceduresProposedAdvisor')?.value,
        } as RsjInstructionEngagementDto,

        engagement: {
          id: this.rsjCommitment?.engagement?.id,
          youngPeopleProject: this.commitmentForm.get('engagement')?.get('youngPeopleProject')?.value,
          youngPeopleSteps: this.commitmentForm.get('engagement')?.get('youngPeopleSteps')?.value,
          proceduresProposedAdvisor: this.commitmentForm.get('engagement')?.get('proceduresProposedAdvisor')?.value,
        } as RsjInstructionEngagementDto,
      } as RsjInstructionCommitmentDto;
    }
    return this.rsjCommitment;
  }

  public syncForm(syncPersonalData: RsjInstructionCommitmentDto): void {
    this.updated = true;
    this.commitmentForm.controls.structureLabel.setValue(syncPersonalData.structureLabel);
    this.commitmentForm.controls.structureAddress.setValue(syncPersonalData.structureAddress);
    this.commitmentForm.controls.structureAdditionalAddress.setValue(syncPersonalData.structureAdditionalAddress);
    this.commitmentForm.controls.structureCity.setValue(syncPersonalData.structureCity);
    this.commitmentForm.controls.structurePostalCode.setValue(syncPersonalData.structurePostalCode);
    this.commitmentForm.controls.structurePhone.setValue(syncPersonalData.structurePhone);
    this.commitmentForm.controls.referentName.setValue(syncPersonalData.referentName);
    this.commitmentForm.controls.referentPhone.setValue(syncPersonalData.referentPhone);
    this.commitmentForm.updateValueAndValidity();
  }

  private initForm(): void {
    this.commitmentForm = this.formBuilder.group({
      structureLabel: [{ value: this.rsjCommitment?.structureLabel, disabled: true }],
      structureAddress: [{ value: this.rsjCommitment?.structureAddress, disabled: true }],
      structureAdditionalAddress: [{ value: this.rsjCommitment?.structureAdditionalAddress, disabled: true }],
      structureCity: [{ value: this.rsjCommitment?.structureCity, disabled: true }],
      structurePostalCode: [{ value: this.rsjCommitment?.structurePostalCode, disabled: true }],
      structurePhone: [{ value: this.rsjCommitment?.structurePhone, disabled: true }],
      referentName: [{ value: this.rsjCommitment?.referentName, disabled: true }],
      referentPhone: [{ value: this.rsjCommitment?.referentPhone, disabled: true }],
      commitmentForNext3Months: [this.rsjCommitment?.commitmentForNext3Months, [Validators.required]],
      situationComment: this.formBuilder.group({
        youngPeopleProject: [this.rsjCommitment?.situationComment?.youngPeopleProject, [Validators.required]],
        youngPeopleSteps: [this.rsjCommitment?.situationComment?.youngPeopleSteps, [Validators.required]],
        proceduresProposedAdvisor: [this.rsjCommitment?.situationComment?.proceduresProposedAdvisor, [Validators.required]],
      }),
      engagement: this.formBuilder.group({
        youngPeopleProject: [this.rsjCommitment?.engagement?.youngPeopleProject, [Validators.required]],
        youngPeopleSteps: [this.rsjCommitment?.engagement?.youngPeopleSteps, [Validators.required]],
        proceduresProposedAdvisor: [this.rsjCommitment?.engagement?.proceduresProposedAdvisor, [Validators.required]],
      }),
    });
    if (this.disabled) {
      this.commitmentForm.disable({ emitEvent: false });
    }
  }

  private initValueChangeHandlers(): void {
    this.commitmentForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => (this.updated = true));
  }

  get engagementGroup(): FormGroup {
    return this.commitmentForm.get('engagement') as FormGroup;
  }

  get situationCommentGroup(): FormGroup {
    return this.commitmentForm.get('situationComment') as FormGroup;
  }
}
